import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { TranslateExcelBusiness, TranslateKeyName, isValidTranslateBusinessId } from '@/coreDomainsModule/translateExcel';

/**
 * 転記処理に使用する取引を区別するためのIDを管理するためのクラス
 */
class TranslateExcelBusinessIdManager {
  /**
   * 転記処理に使用する取引IDの作成を行う
   * @param business 取引
   * @param readExcelType 読み込んだエクセルの種類
   * @returns IDを返す
   */
  public generateBusinessIdFromExcelInput = (business: TranslateExcelBusiness, readExcelType: string): string => {
    // 在庫表には置き場、商品CD、容量、仕入先名、送り先名、売上先名が存在する（現在、仕入先名は無いが今後追加する）
    // 内示は内示エクセルの置き場、商品CD、容量、仕入先名、送り先名、売上先名と在庫表が合致する行に転記
    // 販売実績は販売実績エクセルの置き場、商品CD、容量、仕入先名、送り先名、売上先名と在庫表が合致する行に転記
    // 入荷は入荷エクセルの置き場、商品CD、容量、仕入先名と在庫表が合致する集計行に転記
    // 在庫は在庫エクセルの置き場、商品CD、容量、仕入先名と在庫表が合致する集計行に転記

    // businessId作成に必要な要素が欠けていないか確認
    if (!isValidTranslateBusinessId(business)) return '';
    // 順番が狂うと全く違うものとして扱われてしまうので注意！
    // 置き場、商品CD、容量、仕入先名、送り先名、売上先名の順番
    let output: string =
      (business.warehouseNumber ?? '') +
      (business.businessNumber ?? '') +
      (business.package ?? '') +
      (business.supplierNumber ?? '') +
      (business.salesDestinationNumber ?? '') +
      (business.customerNumber ?? '');
    if (readExcelType === '入荷' || readExcelType === '在庫') {
      // これらに関しては転記先がまとめ行になる都合上、送り先名、売上先名は存在しない
      output =
        (business.warehouseNumber ?? '') + (business.businessNumber ?? '') + (business.package ?? '') + (business.supplierNumber ?? '');
    }
    return output;
  };

  /**
   * 重複した転記処理に使用する取引IDの存在を判定する処理
   * 条件に一致した場合はtrueを返す
   * @param fileType ファイル種別
   * @param formElement IBusinessをラップしたエクセル転記情報
   * @param translateBusinessId 1取引情報の取引ID
   * @returns 重複した取引があるかどうかを返す
   */
  public isFileDuplicated = (fileType: TranslateKeyName, formElement: TranslateExcelBusiness, translateBusinessId: string): boolean => {
    // 読み取りファイルが内示エクセルで、すでに登録されている転記用取引IDが同じ取引の内示を更新しようとしている場合に重複とみなす
    if (
      fileType === '内示' &&
      formElement.customerAnnounce?.every((announce) => announce.year !== 0 && announce.month !== 0) &&
      translateBusinessId
    ) {
      return true;
    }
    if (
      fileType === '日別内示' &&
      formElement.customerAnnounceDaily?.every((announce) => announce.year !== 0 && announce.month !== 0 && announce.day !== 0) &&
      translateBusinessId
    ) {
      return true;
    }
    // 処理を残しつつ、販売実績は表示したくないとのことだったのでコメントアウト
    // if (fileType === '販売実績' && formElement.salesResults?.length !== 0 && translateBusinessId) {
    //   // 読み取りファイルが販売実績エクセル、登録済みの販売実績を更新しようとしている場合に重複とみなす
    //   return true;
    // }
    return false;
  };

  /**
   * 集計行の関連取引群をIBusiness[]としてまとめる関数
   * 登録処理を行なった後のtranslateBusinessを使用する
   * @param registeredBusiness indexDB登録前の取引情報(全て)
   * @param aggregateTranslateExcelBusiness 集計行取引情報
   */
  public makeAggregateLineRelatedBusiness = async (
    registeredBusiness: IBusiness[],
    aggregateTranslateExcelBusiness: TranslateExcelBusiness
  ): Promise<IBusiness[]> => {
    // 関連取引のaggregateTranslateBusinessIdの配列を作成
    const filteredExcelBusinessArray: IBusiness[] = registeredBusiness.filter((business) => {
      // translateBusinessIdに集計行のtranslateBusinessIdが含まれている取引をフィルタリング
      const translateBusinessId: string =
        (business.warehouse ?? '') + (business.businessNumber ?? '') + (business.package ?? '') + (business.supplierName ?? '');
      return translateBusinessId === aggregateTranslateExcelBusiness.translateBusinessId;
    });
    // 関連取引の配列を返す
    return filteredExcelBusinessArray;
  };
}

/**
 * 新規インスタンスを作成して返すだけ
 * @returns TranslateExcelBusinessIdManage 新しいインスタンス
 */
export const getTranslateBusinessIdManager = (): TranslateExcelBusinessIdManager => {
  return new TranslateExcelBusinessIdManager();
};
